type MapObjectType =
  | 'text'
  | 'wall'
  | 'wall_corner'
  | 'wall_t'
  | 'wall_x'
  | 'pointer'
  | 'reception'
  | 'cloakroom'
  | 'restroom'
  | 'lounge';

export type SandboxHostMapElement = {
  id: number;
  type: MapObjectType;
  coord_x: number;
  coord_y: number;
  angle: number;
  content?: string;
  contentStyle?: {
    fontSize: number;
    color: string;
    horizontalAlign: 'flex-start' | 'center' | 'flex-end';
    verticalAlign: 'flex-start' | 'center' | 'flex-end';
  };
};

const elements: SandboxHostMapElement[] = [];
let nextId = 1;

const add = (
  type: MapObjectType,
  coord_x: number,
  coord_y: number,
  angle = 0,
  content?: string,
  color = '#707072',
  fontSize = 12
): void => {
  if (
    elements.some(
      (element) => element.coord_x === coord_x && element.coord_y === coord_y
    )
  ) {
    throw new Error(`Дублирующий объект карты в клетке ${coord_x}:${coord_y}`);
  }
  elements.push({
    id: nextId++,
    type,
    coord_x,
    coord_y,
    angle,
    ...(content
      ? {
          content,
          contentStyle: {
            fontSize,
            color,
            horizontalAlign: 'center' as const,
            verticalAlign: 'center' as const,
          },
        }
      : {}),
  });
};

const addText = (
  x: number,
  y: number,
  text: string,
  color: string,
  size = 12
): void => add('text', x, y, 0, text, color, size);

// Section labels and utility marks from the reference layout.
addText(3, 4, 'QUADRO', '#E24A8D');
addText(7, 3, 'TRIO', '#5762D5');
addText(12, 3, 'DUO-1', '#D81B8C');
addText(7, 6, 'Общий зал', '#5762D5', 16);
addText(12, 6, 'DUO-2', '#D81B8C');
addText(3, 8, 'VIP-DUO', '#72D62C');
addText(3, 10, 'SUPER\nVIP\nDUO', '#F97316');
addText(12, 9, 'АДМИН', '#5762D5');
addText(16, 10, 'ВХОД В\nПОМЕЩЕНИЕ', '#EF4444');
addText(2, 13, 'WC', '#707072');
addText(2, 14, 'WC', '#707072');
addText(2, 15, 'WC', '#707072');
addText(5, 14, 'BOOTCAMP', '#E45B62', 10);
addText(13, 14, 'SOLO\nLEVELING', '#A855F7');

// Walls are one-cell map objects; this ordered matrix mirrors the reference layout.
const wallSpecs: Array<
  ['wall' | 'wall_corner' | 'wall_t', number, number, number]
> = [
  ['wall_corner', 1, 1, 270],
  ['wall', 2, 1, 0],
  ['wall_t', 3, 1, 0],
  ['wall', 4, 1, 0],
  ['wall_t', 5, 1, 0],
  ['wall', 6, 1, 0],
  ['wall', 7, 1, 0],
  ['wall', 8, 1, 0],
  ['wall_t', 9, 1, 0],
  ['wall', 10, 1, 0],
  ['wall', 11, 1, 0],
  ['wall_t', 12, 1, 0],
  ['wall', 13, 1, 0],
  ['wall_corner', 14, 1, 0],
  ['wall', 1, 2, 90],
  ['wall', 3, 2, 90],
  ['wall', 5, 2, 90],
  ['wall', 9, 2, 90],
  ['wall', 12, 2, 90],
  ['wall', 14, 2, 90],
  ['wall', 1, 3, 90],
  ['wall', 3, 3, 90],
  ['wall_corner', 5, 3, 180],
  ['wall', 6, 3, 0],
  ['wall', 8, 3, 0],
  ['wall_corner', 9, 3, 90],
  ['wall', 14, 3, 90],
  ['wall', 1, 4, 90],
  ['wall_t', 12, 4, 270],
  ['wall', 13, 4, 0],
  ['wall_t', 14, 4, 90],
  ['wall', 1, 5, 90],
  ['wall', 3, 5, 90],
  ['wall', 12, 5, 90],
  ['wall', 14, 5, 90],
  ['wall_t', 1, 6, 270],
  ['wall', 2, 6, 0],
  ['wall_t', 3, 6, 90],
  ['wall', 14, 6, 90],
  ['wall', 1, 7, 90],
  ['wall', 3, 7, 90],
  ['wall_t', 12, 7, 180],
  ['wall', 13, 7, 0],
  ['wall_t', 14, 7, 90],
  ['wall', 1, 8, 90],
  ['wall', 14, 8, 90],
  ['wall_t', 1, 9, 270],
  ['wall', 2, 9, 0],
  ['wall_t', 3, 9, 90],
  ['wall', 14, 9, 90],
  ['wall', 1, 10, 90],
  ['wall', 14, 10, 90],
  ['wall', 1, 11, 90],
  ['wall', 3, 11, 90],
  ['wall_t', 1, 12, 270],
  ['wall', 2, 12, 0],
  ['wall_corner', 3, 12, 90],
  ['wall', 14, 12, 90],
  ['wall', 1, 13, 90],
  ['wall', 14, 13, 90],
  ['wall', 1, 14, 90],
  ['wall_corner', 3, 14, 270],
  ['wall', 4, 14, 0],
  ['wall', 6, 14, 0],
  ['wall', 7, 14, 0],
  ['wall', 8, 14, 0],
  ['wall', 9, 14, 0],
  ['wall', 10, 14, 0],
  ['wall_corner', 11, 14, 0],
  ['wall_corner', 12, 14, 270],
  ['wall', 14, 14, 90],
  ['wall', 1, 15, 90],
  ['wall', 3, 15, 90],
  ['wall', 11, 15, 90],
  ['wall', 12, 15, 90],
  ['wall', 14, 15, 90],
  ['wall_corner', 1, 16, 180],
  ['wall', 2, 16, 0],
  ['wall_t', 3, 16, 180],
  ['wall', 4, 16, 0],
  ['wall', 5, 16, 0],
  ['wall', 6, 16, 0],
  ['wall', 7, 16, 0],
  ['wall', 8, 16, 0],
  ['wall', 9, 16, 0],
  ['wall', 10, 16, 0],
  ['wall_t', 11, 16, 180],
  ['wall_t', 12, 16, 180],
  ['wall', 13, 16, 0],
  ['wall_corner', 14, 16, 90],
];
wallSpecs.forEach(([type, x, y, angle]) => add(type, x, y, angle));

// Supported renderer icons used in the empty utility cells.
add('lounge', 10, 2);
add('lounge', 11, 2);
add('reception', 13, 8);
add('reception', 13, 9);
add('cloakroom', 13, 10);
add('cloakroom', 13, 12);
add('cloakroom', 4, 15);
add('pointer', 13, 11, 180);
add('pointer', 14, 11, 180);
add('pointer', 15, 11, 180);

export const createHostMapLayout = (): {
  elements: SandboxHostMapElement[];
} => ({
  elements: elements.map((element) => ({
    ...element,
    ...(element.contentStyle
      ? { contentStyle: { ...element.contentStyle } }
      : {}),
  })),
});
